import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { RelationalOperator } from '@mtna/data-core-ts';
import { MtnaPopoverConfig } from '@mtna/core-angular';
import { Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { BehaviorSubject, ReplaySubject, Subscription } from 'rxjs';

import { BaseFilter } from '../core/models/filter/base-filter.model';
import { RdsVariableUtil } from '../core/utils/variable-util';
import { ALL_MEASURE_OPTION_TYPES, MeasureOptionType } from '../measure-select/measure-option.model';
import { MeasureSelection } from '../measure-select/measure-selection.model';

export interface RdsTabulationDimensionsForm<V extends Variable | VariableSummary = Variable> {
  columns?: Array<V> | null;
  filters?: Array<BaseFilter<V>> | null;
  rows: Array<V>;
  weights?: Array<V> | null;
  measures?: MeasureSelection<V>[] | null;
}

@Component({
  selector: 'rds-c-dimension-selection',
  templateUrl: './dimension-selection.component.html',
  styleUrls: ['./dimension-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsDimensionSelectionComponent implements OnDestroy, OnInit {
  static ngAcceptInputType_supportedOperators: RelationalOperator[] | null | undefined;
  static ngAcceptInputType_tabulating: BooleanInput;

  /** Selected column dimensions */
  @Input()
  get columns(): Array<VariableSummary | Variable> {
    return this._columns;
  }
  set columns(columns: Array<VariableSummary | Variable>) {
    this._columns = columns;
    // If the form has been initialized and the columns are set,
    // update the form value
    const colCtrl = this.form?.get('columns');
    if (colCtrl) {
      colCtrl.setValue(
        this.columns ? this.columns.filter((c) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, c))) : null
      );
      colCtrl.markAsDirty();
    }

    if (!!this.columns && this.columns.length) {
      this.disabledRowOptions$.next(this.columns);
    }
  }
  private _columns: Array<VariableSummary | Variable> = [];

  /** Popover config for column selection */
  @Input() columnsConfig = new MtnaPopoverConfig(
    $localize`Columns`,
    $localize`Categorical variables that will act as column dimensions in the tabulation table.`
  );

  /**
   * NgStyle config for the dialog content's filter component, defaults to `{ height: '300px' }`
   * @example
   * { 'maxHeight': '300px' }
   */
  @Input() dialogFilterStyles: { [key: string]: string } = { height: '300px' };
  /** Optional filter dialog min-width */
  @Input() dialogMinWidth?: string;

  /** Optional filter */
  @Input() get filters(): Array<BaseFilter<Variable | VariableSummary>> {
    return this._filters;
  }
  set filters(filters: Array<BaseFilter<Variable | VariableSummary>>) {
    this._filters = filters;
    // If the form has been initialized and the filter is set,
    // update the form value
    const filterCtrl = this.form?.get('filters');
    if (filterCtrl) {
      filterCtrl.setValue(
        !!this.filters &&
          !!this.filters.every((f) => !!f.variable && this.variables.some((v) => RdsVariableUtil.compareVariables(v, f.variable)))
          ? [...this.filters]
          : null
      );
      filterCtrl.markAsDirty();
    }
  }

  private _filters: Array<BaseFilter<Variable | VariableSummary>> = [];

  /** Popover config for filter select */
  @Input() filterConfig: MtnaPopoverConfig = new MtnaPopoverConfig(
    $localize`Filter`,
    $localize`Filters the data on any available variable (logically joined using the AND operator if more than one is specified).`,
    'after'
  );
  /** Maximum number of column dimensions */
  @Input() maxColumns = 1;
  /** Maximum number of row dimensions */
  @Input() maxRows = 1;

  /** Selected meausre */
  @Input()
  get measures(): MeasureSelection<Variable | VariableSummary>[] {
    return this._measures;
  }
  set measures(measures: MeasureSelection<Variable | VariableSummary>[]) {
    this._measures = measures;
    if (!!this.form && !!this.measureVariables) {
      // If the form has been initialized and the measures are set,
      // update the form value
      const measureCtrl = this.form.get('measures');
      if (measureCtrl) {
        measureCtrl.setValue(this.measures);
        measureCtrl.markAsDirty();
      }
    }
  }
  private _measures: MeasureSelection<Variable | VariableSummary>[] = [];

  /** Popover config for measure select */
  @Input() measureConfig: MtnaPopoverConfig = new MtnaPopoverConfig(
    $localize`Measure`,
    $localize`Count/Percentage of records, or statistical aggregation (e.g. sum, average) of numeric variables, rolled up by dimensions, that define the content of the table cell values.`,
    'after'
  );

  /** Optional array of measure variables to choose from */
  @Input()
  get measureVariables(): Array<Variable | VariableSummary> | null | undefined {
    return this._measureVariables;
  }
  set measureVariables(variables: Array<Variable | VariableSummary> | null | undefined) {
    if (variables) {
      this._measureVariables = variables;
      if (!!this.form && !!this.measureVariables && !!this.measures) {
        const measureCtrl = this.form.get('measures');
        if (measureCtrl) {
          measureCtrl.setValue(this.measures);
          measureCtrl.markAsDirty();
        }
      }
    }
  }
  private _measureVariables: Array<Variable | VariableSummary> | null | undefined = [];

  /** Whether the user can select multiple filter codes */
  @Input() multiFilterCodes = false;
  /** Whether the user can select multiple filter variables */
  @Input() multiFilterVariables = false;
  /** Selected row dimensions */
  @Input()
  get rows(): Array<Variable | VariableSummary> {
    return this._rows;
  }
  set rows(rows: Array<Variable | VariableSummary>) {
    this._rows = rows;
    // If the form has been initialized and the rows are set,
    // update the form value
    const rowCtrl = this.form?.get('rows');
    if (rowCtrl) {
      rowCtrl.setValue(this.rows ? this.rows.filter((c) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, c))) : null);
      rowCtrl.markAsDirty();
    }

    if (!!this.rows && this.rows.length) {
      this.disabledColumnOptions$.next(this.rows);
    }
  }
  private _rows: Array<Variable | VariableSummary> = [];

  /** Popover config for row selecttion */
  @Input() rowsConfig = new MtnaPopoverConfig(
    $localize`Rows`,
    $localize`Categorical variables that will act as row dimensions in the tabulation table. A minimum of one row is required to tabulate.`
  );

  /** The supported `RelationalOperator`s. i.e. what operaters does the current data product support. */
  @Input()
  set supportedOperators(operators: RelationalOperator[] | undefined) {
    this._supportedOperators = operators || undefined;
  }
  get supportedOperators(): RelationalOperator[] | undefined {
    return this._supportedOperators;
  }
  private _supportedOperators: RelationalOperator[] | undefined;

  /** Whether currently tabulating */
  @Input()
  get tabulating(): boolean {
    return this._tabulating;
  }
  set tabulating(tabulating: boolean) {
    this._tabulating = coerceBooleanProperty(tabulating);
    if (tabulating && !!this.form) {
      this.form.disable();
    } else if (!tabulating && !!this.form) {
      this.form.enable();
    }
  }
  private _tabulating = false;

  /** List of variables available to select */
  @Input() get variables(): Array<VariableSummary> {
    return this._variables;
  }
  set variables(variables: Array<VariableSummary>) {
    this._variables = variables;
    if (!!this.form && !!this.variables) {
      // When variables change, form is reset to a default state
      const defaultState: RdsTabulationDimensionsForm = {
        filters:
          !!this.filters &&
          this.filters.every((f) => !!f.variable && this.variables.some((v) => RdsVariableUtil.compareVariables(v, f.variable)))
            ? [...this.filters]
            : undefined,
        rows: this.rows ? this.rows.filter((r) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, r))) : [],
        columns: this.columns ? this.columns.filter((c) => this.variables.some((v) => RdsVariableUtil.compareVariables(v, c))) : undefined,
        weights: this.weights
          ? this.weights.filter((w) => this.weightedVariables?.some((v) => RdsVariableUtil.compareVariables(v, w)))
          : undefined,
        measures: this.measures ? this.measures : undefined,
      };
      // Reset the form using the default state values
      this.form.reset(defaultState);
      this.determineIfFormIsEmpty();
    }
  }
  private _variables: Array<VariableSummary> = [];

  /** Selected variable weights */
  @Input()
  get weights(): Array<Variable | VariableSummary> | null | undefined {
    return this._weights;
  }
  set weights(weights: Array<Variable | VariableSummary> | null | undefined) {
    this._weights = weights;
    if (!!this.form && !!this.weightedVariables) {
      // If the form has been initialized and the weights are set,
      // update the form value
      const weightCtrl = this.form.get('weights');
      if (weightCtrl) {
        weightCtrl.setValue(
          this.weights ? this.weights.filter((w) => this.weightedVariables?.some((v) => RdsVariableUtil.compareVariables(v, w))) : null
        );
        weightCtrl.markAsDirty();
      }
    }
  }
  private _weights: Array<Variable | VariableSummary> | null | undefined = [];

  /** Popover config for weight select */
  @Input() weightsConfig: MtnaPopoverConfig = new MtnaPopoverConfig(
    $localize`Weighted Variables`,
    $localize`Add variables to weight the tabulation`
  );
  /** Optional array of weighted variables to choose from */
  @Input()
  get weightedVariables(): Array<Variable | VariableSummary> | null | undefined {
    return this._weightedVariables;
  }
  set weightedVariables(variables: Array<Variable | VariableSummary> | null | undefined) {
    this._weightedVariables = variables;
    if (!!this.form && !!this.weightedVariables && this.weights) {
      const weightCtrl = this.form.get('weights');
      if (weightCtrl) {
        weightCtrl.setValue(
          this.weights ? this.weights.filter((w) => this.weightedVariables?.some((v) => RdsVariableUtil.compareVariables(v, w))) : null
        );
        weightCtrl.markAsDirty();
      }
    }
  }
  private _weightedVariables: Array<Variable | VariableSummary> | null | undefined;

  /** The selected TabulationDimensions to be tabulated */
  @Output() tabulate = new EventEmitter<RdsTabulationDimensionsForm>();

  disabledColumnOptions$ = new BehaviorSubject<Variable[] | VariableSummary[]>([]);
  disabledRowOptions$ = new BehaviorSubject<Variable[] | VariableSummary[]>([]);
  form: FormGroup | undefined;
  formValueChangeSub = new Subscription();
  /**
   * Subject that emits the latest selected variables accross all dimension controls.
   * Used to prevent variables from being selected multiple times.
   */
  selectedVariables$ = new ReplaySubject<string[]>(1);

  _allMeasureTypes = [...ALL_MEASURE_OPTION_TYPES];
  _countAndPercentMeasureTypes: Array<MeasureOptionType> = ['COUNT', 'PERCENT'];
  /** Whether a form is empty or not. */
  _isFormEmpty = true;
  /** Placeholders for chipsAriaLabel in row */
  _selectedRowPlaceholder = $localize`Selected row`;
  _selectedRowsPlaceholder = $localize`Selected rows`;
  /** Placeholders for selectPlaceholder in row */
  _selectARowPlaceholder = $localize`Select a row`;
  _selectRowsPlaceholder = $localize`Select rows`;
  /** Placeholders for selectLabel in row */
  _rowLabelPlaceholder = $localize`Row`;
  _rowsLabelPlaceholder = $localize`Rows`;
  /** Placeholders for chipsAriaLabel in column */
  _selectedColumnPlaceholder = $localize`Selected column`;
  _selectedColumnsPlaceholder = $localize`Selected columns`;
  /** Placeholders for selectLabel in column */
  _columnPlaceholder = $localize`Column`;
  _columnsPlaceholder = $localize`Columns`;
  /** Placeholders for selectPlaceholder in column */
  _selectAColumnPlaceholder = $localize`Select a column`;
  _selectColumnsPlaceholder = $localize`Select columns`;

  constructor(private cdr: ChangeDetectorRef, private fb: FormBuilder, private liveAnnouncer: LiveAnnouncer) {}

  determineIfFormIsEmpty() {
    const formValues: RdsTabulationDimensionsForm = this.form?.value;
    if (
      formValues.columns?.length &&
      formValues.filters?.length &&
      formValues.measures?.length &&
      formValues.rows?.length &&
      formValues.weights?.length
    ) {
      this._isFormEmpty = false;
    } else {
      this._isFormEmpty = true;
    }
  }

  ngOnDestroy(): void {
    this.formValueChangeSub.unsubscribe();
  }

  ngOnInit() {
    const columnValidators = this.maxColumns > 0 ? [maximumSelection(this.maxColumns)] : null;
    const rowValidators = [maximumSelection(this.maxRows), Validators.required];
    this.form = this.fb.group({
      columns: this.fb.control(this.columns ? [...this.columns] : null, columnValidators),
      rows: this.fb.control(this.rows ? [...this.rows] : null, rowValidators),
      filters: this.fb.control(this.filters ? [...this.filters] : null),
      weights: this.fb.control(this.weights ? [...this.weights] : null),
      measures: this.fb.control(this.measures.length ? this.measures : null),
    });

    this.determineIfFormIsEmpty();

    // Disable the form if tabulating
    if (this.tabulating) {
      this.form.disable();
    }

    // Initial disabled
    if (!!this.rows && this.rows.length) {
      this.disabledColumnOptions$.next(this.rows);
    }
    if (!!this.columns && this.columns.length) {
      this.disabledRowOptions$.next(this.columns);
    }

    // emit initial selected values
    this._emitSelectedVariables(this.form.value);
    // each time the form changes, emit selected variables
    this.formValueChangeSub.add(this.form.valueChanges.subscribe((valueChanges) => this._emitSelectedVariables(valueChanges)));
  }

  resetForm() {
    this.form?.reset();
    this.disabledRowOptions$.next([]);
    this.disabledColumnOptions$.next([]);
    this._isFormEmpty = true;
  }

  submitForm() {
    if (this.form?.valid) {
      this.tabulate.emit(this.form.value);
    } else {
      this.liveAnnouncer.announce($localize`The tabulation parameters are invalid, please check them and try again.`);
    }
  }

  /**
   * Get all the selected variable IDs and put them in the selected array
   * to disable these from being selected multiple times.
   * exclude filter and weights
   */
  private _emitSelectedVariables(valueChanges: { [key: string]: unknown }) {
    const newSelections = new Array<string>();
    for (const prop in valueChanges) {
      if (Object.prototype.hasOwnProperty.call(valueChanges, prop) && prop !== 'filter' && prop !== 'weights' && !!valueChanges[prop]) {
        // Must be measure, rows, or columns
        const v = valueChanges[prop] as Variable | VariableSummary;
        if (v.id) {
          newSelections.push(v.id);
        }
      }
    }
    this.selectedVariables$.next(newSelections);
    this.cdr.markForCheck();
  }
}

/**
 * Function for validating max selections limits
 * @param limit upper limit to check against
 */
function maximumSelection(limit = 1): ValidatorFn {
  const validator: ValidatorFn = (ctrl: AbstractControl) => {
    const totalSelected = ctrl.value ? ctrl.value.length : 0;
    return totalSelected <= limit
      ? null
      : {
          maxSelected: $localize`Select no more than ${limit}:As in select no more than 4:`,
        };
  };
  return validator;
}
